<template>
  <div class="rb-portal" :dir="lang === 'en' ? 'ltr' : 'rtl'">
    <portal-sprite/>
    <div class="portal-shell">
      <aside class="portal-sidebar" :aria-label="$t('intake.portal.navAria')">
        <div class="portal-brand">
          <router-link class="logo" :to="home">{{ $t('intake.portal.brand') }}</router-link>
          <div class="portal-person">
            <span class="avatar" aria-hidden="true">{{ personAvatar }}</span>
            <span><b>{{ personName }}</b><small>{{ $t('intake.portal.personRole') }}</small></span>
          </div>
        </div>
        <nav class="portal-nav" :aria-label="$t('intake.portal.navSectionsAria')">
          <router-link
              v-for="item in sideNav"
              :key="item.id"
              :to="item.to"
              :aria-current="activeNav === item.id ? 'page' : null"
          >
            <svg class="icon" aria-hidden="true"><use :href="'#' + item.icon"/></svg>
            <span>{{ $t(item.label) }}</span>
            <small v-if="item.id === 'notifications' && unreadNotifications > 0" class="notification-count" role="status">{{ unreadNotifications }}</small>
          </router-link>
        </nav>
        <div class="portal-side-foot">
          <router-link :to="{name: 'legalDocument', params: {lang, documentId: 'terms'}}">{{ lang === 'fa' ? 'شرایط استفاده' : 'Terms of use' }}</router-link>
          <router-link :to="{name: 'legalDocument', params: {lang, documentId: 'privacy'}}">{{ lang === 'fa' ? 'حریم خصوصی' : 'Privacy notice' }}</router-link>
          <button class="portal-logout" type="button" @click="logout">{{ $t('menu.exit') }}</button>
          <router-link :to="consentLink">
            <svg class="icon icon-sm" aria-hidden="true"><use href="#i-shield"/></svg>
            {{ $t('intake.portal.sideFoot') }}
          </router-link>
        </div>
      </aside>

      <div :class="['portal-main', chatLayout ? 'chat-layout' : '']">
        <header :class="['portal-header', chatLayout ? 'chat-header' : '']">
          <div v-if="chatLayout" class="chat-peer">
            <span class="avatar" aria-hidden="true"><img v-if="peerAvatar && !peerAvatarFailed" :src="peerAvatar" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:inherit" @error="peerAvatarFailed = true"><template v-else>{{ peerInitials }}</template></span>
            <div>
              <p class="eyebrow">{{ $t('intake.portal.chatEyebrow') }}</p>
              <h1>{{ title }}</h1>
              <p class="chat-sla">{{ $t('intake.portal.chatSla') }}</p>
            </div>
          </div>
          <div v-else>
            <p class="eyebrow">{{ eyebrow }}</p>
            <h1>{{ title }}</h1>
            <p v-if="subtitle">{{ subtitle }}</p>
          </div>
          <router-link class="profile-chip" :to="profileLink" :aria-label="$t('intake.portal.profileAria')">
            <svg class="icon" aria-hidden="true"><use href="#i-user"/></svg>
          </router-link>
        </header>
        <router-view @chat-peer="setChatPeer"/>
      </div>
    </div>

    <div v-if="stickyCta" class="mobile-sticky-cta">
      <router-link class="btn btn-primary" :to="stickyCta.to">{{ $t(stickyCta.label) }}</router-link>
    </div>

    <nav class="mobile-bottom-nav" :style="{'--portal-nav-count': bottomNav.length}" :aria-label="$t('intake.portal.mobileNavAria')">
      <router-link
          v-for="item in bottomNav"
          :key="item.id"
          :to="item.to"
          :aria-current="activeNav === item.id ? 'page' : null"
      >
        <svg class="icon icon-nav" aria-hidden="true"><use :href="'#' + item.icon"/></svg>
        <span>{{ $t(item.short) }}</span>
      </router-link>
    </nav>
  </div>
</template>

<script>
import PortalSprite from '@/components/portal/PortalSprite.vue'
import store from '@/store'
import {reserveSessionRoute, walletSessionCredit} from '@/intake/portalApi'
import {notificationState, refreshNotificationUnreadCount} from '@/intake/clientNotifications'

const NAV = [
  {id:'notifications',icon:'i-bell',label:'intake.treatment.notifyTitle',short:'intake.treatment.notifyTitle',toName:'intakeNotify'},
  {id: 'home', icon: 'i-home', label: 'intake.portal.navHome', short: 'intake.portal.navHomeShort', toName: 'portalHome'},
  {id: 'sessions', icon: 'i-calendar', label: 'intake.portal.navSessions', short: 'intake.portal.navSessionsShort', toName: 'portalSessions'},
  {id: 'progress', icon: 'i-path', label: 'intake.portal.navProgress', short: 'intake.portal.navProgressShort', toName: 'portalProgress'},
  {id: 'chat', icon: 'i-message', label: 'intake.portal.navChat', short: 'intake.portal.navChatShort', toName: 'portalChat'},
  {id: 'payments', icon: 'i-card', label: 'intake.portal.navPayments', short: 'intake.portal.navPaymentsShort', toName: 'portalPayments'},
  {id: 'support', icon: 'i-bell', label: 'intake.portal.navSupport', short: 'intake.portal.navSupportShort', toName: 'portalSupport'},
  {id: 'services', icon: 'i-user', label: 'intake.portal.navServices', short: 'intake.portal.navServicesShort', toName: 'portalServices'},
]

function readCachedProfile() {
  try {
    const fromStore = store.getters && store.getters.getProfile
    if (fromStore && typeof fromStore === 'object') return fromStore
  } catch (e) { /* ignore */ }
  try {
    const raw = localStorage.getItem('profile')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && parsed.data && typeof parsed.data === 'object') return parsed.data
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (e) {
    return null
  }
}

function displayName(profile, lang) {
  if (!profile || typeof profile !== 'object') return ''
  const full = String(profile.full_name || '').trim()
  if (full) return full
  const first = String(profile.firstname || profile.name || '').trim()
  const last = String(profile.lastname || profile.family || '').trim()
  const joined = [first, last].filter(Boolean).join(' ')
  if (joined) return joined
  if (lang === 'en') {
    const en = [profile.en_name, profile.en_family].filter(Boolean).join(' ').trim()
    if (en) return en
  }
  return ''
}

function firstName(profile) {
  if (!profile || typeof profile !== 'object') return ''
  const first = String(profile.firstname || profile.name || '').trim()
  if (first) return first.split(/\s+/)[0]
  const full = displayName(profile, 'fa')
  return full ? full.split(/\s+/)[0] : ''
}

function initials(profile, lang) {
  const name = displayName(profile, lang)
  if (!name) return ''
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).slice(0, 2)
  return name.slice(0, 2)
}

export default {
  name: 'PortalView',
  components: {PortalSprite},
  data() {
    return {profile: readCachedProfile(), chatPeer: null, peerAvatarFailed: false}
  },
  computed: {
    unreadNotifications() { return notificationState.unreadCount },
    lang() {
      return this.$route.params.lang || 'fa'
    },
    home() {
      return {name: 'portalHome', params: {lang: this.lang}}
    },
    profileLink() {
      return {name: 'setting', params: {lang: this.lang}}
    },
    consentLink() {
      return {name: 'intakeConsent', params: {lang: this.lang}}
    },
    meta() {
      return this.$route.meta || {}
    },
    activeNav() {
      return this.meta.nav || null
    },
    chatLayout() {
      return !!this.meta.chat
    },
    peerInitials() { return initials(this.chatPeer, this.lang) || '?' },
    peerAvatar() { return this.chatPeer && this.chatPeer.avatar || '' },
    personName() {
      return displayName(this.profile, this.lang) || this.$t('intake.portal.personNameFallback')
    },
    personAvatar() {
      const value = initials(this.profile, this.lang)
      return value || this.$t('intake.portal.avatarFallback')
    },
    greetingName() {
      return firstName(this.profile) || this.personName
    },
    title() {
      const key = this.meta.titleKey || 'intake.portal.homeTitle'
      if (key === 'intake.portal.homeTitle') {
        return this.$t('intake.portal.homeTitleLive', {name: this.greetingName})
      }
      if (this.chatLayout && key === 'intake.portal.therapistName') {
        const live = displayName(this.chatPeer, this.lang)
        if (live) return live
      }
      return this.$t(key)
    },
    eyebrow() {
      return this.$t(this.meta.eyebrowKey || 'intake.portal.eyebrow')
    },
    subtitle() {
      return this.meta.subtitleKey ? this.$t(this.meta.subtitleKey) : ''
    },
    stickyCta() {
      if (!this.meta.stickyCta) return null
      const credit = walletSessionCredit(this.profile)
      if (this.meta.stickyCta.to === 'portalPackage' && credit > 0) {
        return {
          to: reserveSessionRoute(this.lang),
          label: 'intake.portal.payCreditCta',
        }
      }
      return {
        to: {name: this.meta.stickyCta.to, params: {lang: this.lang}},
        label: this.meta.stickyCta.label,
      }
    },
    sideNav() {
      return NAV.map((n) => ({
        ...n,
        to: {name: n.toName, params: {lang: this.lang}},
      }))
    },
    bottomNav() {
      return this.sideNav.filter(item => ['home', 'sessions', 'progress', 'chat', 'services'].includes(item.id))
    },
  },
  watch: {
    '$route.fullPath'() { this.setChatPeer(null); refreshNotificationUnreadCount().catch(()=>{}) },
    '$store.getters.getProfile'(profile) { if (profile) this.profile = profile },
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    setChatPeer(peer) { this.chatPeer = peer; this.peerAvatarFailed = false },
    bootstrap() {
      refreshNotificationUnreadCount().catch(()=>{})
      const token = localStorage.getItem('token') || (store.state && store.state.token)
      if (!token) {
        this.profile = readCachedProfile()
        return
      }
      store.dispatch('generalRequest', {
        type: 'auth',
        name: 'getProfile',
        paramsQuery: {},
        body: {},
        headers: {},
      }).then(() => {
        this.profile = readCachedProfile()
      }).catch(() => {
        this.profile = readCachedProfile()
      })
    },
  },
}
</script>
